import { useApiMutation } from './useApiMutation';
import { useApiQuery } from './useApiQuery';
import { ledgerQueryKeys } from './useGeneralLedger';
import { queryKeys } from './queryKeys';
import { bankIncomingQueryKeys } from './useBankIncoming';
import { BankReconciliationService } from '@/services/general-ledger/bankReconciliationService';
import { fundTransactionService } from '@/services/fundTransactionService';
import type { PageResult } from '@/types/drawdown';
import type { TransactionBankMatchView } from '@/types/bank-reconciliation';
import type { TransactionActivityEntry } from '@/types/transaction-activity';
import type {
  LpTransferDraftChange,
  LpTransferDraftUpdate,
  LpTransferImpact,
  LpTransferUpdateResponse,
} from '@/types/investor';
import type { APIError } from '@/utils/api-client';
import type {
  AddBankEntryRequest,
  BankEntryResponse,
  FundTransactionCategory,
  FundTransactionDetail,
  FundTransactionDocument,
  FundTransactionDocumentCreateResponse,
  FundTransactionDocumentLinkRequest,
  FundTransactionGLTransaction,
  FundTransactionStatus,
  FundTransactionSummary,
  FundTransactionType,
  ManualApproveRequest,
  ReconciliationStateResponse,
  UpdateBankEntryRequest,
} from '@/types/fund-transactions';

export const useFundTransactions = (
  fundId: string,
  params?: {
    transaction_type?: FundTransactionType;
    transaction_category?: FundTransactionCategory;
    status?: FundTransactionStatus;
    date_from?: string;
    date_to?: string;
    sort_order?: 'asc' | 'desc';
    page?: number;
    page_size?: number;
  },
  enabled: boolean = true
) => {
  return useApiQuery<PageResult<FundTransactionSummary>>(
    queryKeys.fundTransactions.list(fundId, params),
    () => fundTransactionService.list(fundId, params ?? {}),
    { enabled: !!fundId && enabled }
  );
};

export const useFundTransaction = (fundId: string, id: string) => {
  return useApiQuery<FundTransactionDetail>(
    queryKeys.fundTransactions.detail(fundId, id),
    () => fundTransactionService.detail(id),
    { enabled: !!fundId && !!id }
  );
};

// A portfolio transaction's log is shared with the company page, whose writes
// don't refresh fund keys: refetch on every mount.
export const useFundTransactionActivity = (fundId: string, id: string) =>
  useApiQuery<TransactionActivityEntry[]>(
    queryKeys.fundTransactions.activity(fundId, id),
    () => fundTransactionService.listActivity(fundId, id),
    { enabled: !!fundId && !!id, staleTime: 0 }
  );

export const useFundTransactionReconciliation = (
  fundId: string,
  id: string,
  enabled: boolean = true
) => {
  return useApiQuery<ReconciliationStateResponse>(
    queryKeys.fundTransactions.reconciliation(fundId, id),
    () => fundTransactionService.getReconciliation(id),
    { enabled: enabled && !!fundId && !!id }
  );
};

export const useFundTransactionBankEntries = (fundId: string, id: string) => {
  return useApiQuery<BankEntryResponse[]>(
    queryKeys.fundTransactions.bankEntries(fundId, id),
    () => fundTransactionService.listBankEntries(fundId, id),
    { enabled: !!fundId && !!id }
  );
};

export const useFundTransactionJournalEntries = (
  fundId: string,
  id: string,
  enabled: boolean = true
) => {
  return useApiQuery<FundTransactionGLTransaction[]>(
    queryKeys.fundTransactions.journalEntries(fundId, id),
    () => fundTransactionService.getJournalEntries(id),
    { enabled: enabled && !!fundId && !!id }
  );
};

export const useGenerateManagementFeeDrafts = (fundId: string) => {
  return useApiMutation(
    () => fundTransactionService.generateManagementFeeDrafts(fundId),
    {
      invalidateKeys: [queryKeys.fundTransactions.lists(fundId)],
      showSuccessToast: false,
    }
  );
};

export const useAddFundTransactionBankEntry = (
  fundId: string,
  transactionId: string
) => {
  return useApiMutation<BankEntryResponse, unknown, AddBankEntryRequest>(
    (data) => fundTransactionService.addBankEntry(fundId, transactionId, data),
    {
      invalidateKeys: [
        queryKeys.fundTransactions.bankEntries(fundId, transactionId),
        queryKeys.fundTransactions.reconciliation(fundId, transactionId),
        queryKeys.fundTransactions.detail(fundId, transactionId),
      ],
      successMessage: 'Bank entry added',
    }
  );
};

export const useUpdateFundTransactionBankEntry = (
  fundId: string,
  transactionId: string
) => {
  return useApiMutation<
    BankEntryResponse,
    unknown,
    { entryId: string; data: UpdateBankEntryRequest }
  >(
    ({ entryId, data }) =>
      fundTransactionService.updateBankEntry(
        fundId,
        transactionId,
        entryId,
        data
      ),
    {
      invalidateKeys: [
        queryKeys.fundTransactions.bankEntries(fundId, transactionId),
        queryKeys.fundTransactions.reconciliation(fundId, transactionId),
      ],
      successMessage: 'Bank entry updated',
    }
  );
};

export const useDeleteFundTransactionBankEntry = (
  fundId: string,
  transactionId: string
) => {
  return useApiMutation<void, unknown, string>(
    (entryId) =>
      fundTransactionService.deleteBankEntry(fundId, transactionId, entryId),
    {
      invalidateKeys: [
        queryKeys.fundTransactions.bankEntries(fundId, transactionId),
        queryKeys.fundTransactions.reconciliation(fundId, transactionId),
        queryKeys.fundTransactions.activity(fundId, transactionId),
      ],
      successMessage: 'Bank entry removed',
    }
  );
};

export const useFundTransactionBankMatching = (
  fundId: string,
  id: string,
  enabled: boolean = true
) => {
  return useApiQuery<TransactionBankMatchView>(
    queryKeys.fundTransactions.bankMatching(fundId, id),
    () => BankReconciliationService.getTransactionBankMatching(fundId, id),
    { enabled: enabled && !!fundId && !!id }
  );
};

export const useMatchBankIncomingToFundTransaction = (
  fundId: string,
  transactionId: string
) => {
  return useApiMutation<unknown, unknown, string>(
    (incomingId) =>
      BankReconciliationService.matchIncoming(
        fundId,
        incomingId,
        transactionId
      ),
    {
      invalidateKeys: [
        queryKeys.fundTransactions.bankMatching(fundId, transactionId),
        queryKeys.fundTransactions.reconciliation(fundId, transactionId),
        queryKeys.fundTransactions.bankEntries(fundId, transactionId),
        queryKeys.fundTransactions.detail(fundId, transactionId),
        queryKeys.fundTransactions.lists(fundId),
        ledgerQueryKeys.bankIncoming(fundId),
        bankIncomingQueryKeys.all,
      ],
      successMessage: 'Bank line matched - bank confirmation recorded',
    }
  );
};

export const useAdminApproveFundTransaction = (
  fundId: string,
  transactionId: string
) => {
  return useApiMutation(
    () => fundTransactionService.adminApprove(transactionId),
    {
      invalidateKeys: [
        queryKeys.fundTransactions.detail(fundId, transactionId),
        queryKeys.fundTransactions.reconciliation(fundId, transactionId),
        queryKeys.fundTransactions.lpAllocationsAll(fundId),
        queryKeys.fundTransactions.lists(fundId),
        // Posting a fund cost payment creates its recognition rows.
        queryKeys.fundCosts.all,
        // Approving posts to the ledger and recomputes what it posts, so
        // every ledger-sourced read -- balances, and the period-end working
        // that still shows what the draft previewed -- is now behind.
        ledgerQueryKeys.all,
      ],
      successMessage: 'Transaction posted to the ledger',
    }
  );
};

export const useManualApproveFundTransaction = (
  fundId: string,
  transactionId: string
) => {
  return useApiMutation<
    ReconciliationStateResponse,
    unknown,
    ManualApproveRequest
  >((data) => fundTransactionService.manualApprove(transactionId, data), {
    invalidateKeys: [
      queryKeys.fundTransactions.detail(fundId, transactionId),
      queryKeys.fundTransactions.reconciliation(fundId, transactionId),
      queryKeys.fundTransactions.lpAllocationsAll(fundId),
      queryKeys.fundTransactions.lists(fundId),
      queryKeys.fundCosts.all,
      ledgerQueryKeys.all,
    ],
    successMessage: 'Manual approval recorded',
  });
};

export const useFundTransactionDocuments = (
  fundId: string,
  id: string,
  enabled: boolean = true
) =>
  useApiQuery<FundTransactionDocument[]>(
    queryKeys.fundTransactions.documents(fundId, id),
    () => fundTransactionService.listDocuments(fundId, id),
    { enabled: enabled && !!fundId && !!id }
  );

const documentChangeKeys = (fundId: string, id: string) => [
  queryKeys.fundTransactions.documents(fundId, id),
  queryKeys.fundTransactions.reconciliation(fundId, id),
  queryKeys.fundTransactions.detail(fundId, id),
];

export const useLinkFundTransactionDocument = (fundId: string, id: string) =>
  useApiMutation<
    FundTransactionDocumentCreateResponse,
    unknown,
    FundTransactionDocumentLinkRequest
  >((data) => fundTransactionService.linkDocument(fundId, id, data), {
    invalidateKeys: documentChangeKeys(fundId, id),
    successMessage: 'Document attached',
  });

export const useUnlinkFundTransactionDocument = (fundId: string, id: string) =>
  useApiMutation<void, unknown, string>(
    (linkId) => fundTransactionService.unlinkDocument(fundId, id, linkId),
    {
      invalidateKeys: documentChangeKeys(fundId, id),
      successMessage: 'Document removed',
    }
  );

// Deleting or editing a draft transfer recalculates later draft drawdowns,
// distributions and investments.
const lpTransferRecalculationKeys = [
  queryKeys.investors.all,
  queryKeys.funds.all,
  queryKeys.drawdowns.all,
  queryKeys.distributions.all,
  ledgerQueryKeys.all,
];

// Not the deleted row's own keys: its page is still mounted until the caller
// navigates away, and a refetch would 404.
export const useDeleteLpTransferDraft = (fundId: string) =>
  useApiMutation<void, APIError, string>(
    (fundTransactionId) =>
      fundTransactionService.deleteLpTransferDraft(fundId, fundTransactionId),
    {
      invalidateKeys: [
        ...lpTransferRecalculationKeys,
        queryKeys.fundTransactions.lists(fundId),
        queryKeys.fundTransactions.lpAllocationsAll(fundId),
      ],
      successMessage: 'Draft transfer deleted',
    }
  );

export const useLpTransferDeleteImpact = (
  fundId: string,
  fundTransactionId: string
) =>
  useApiQuery<LpTransferImpact>(
    queryKeys.fundTransactions.lpTransferDeleteImpact(
      fundId,
      fundTransactionId
    ),
    () =>
      fundTransactionService.getLpTransferDeleteImpact(
        fundId,
        fundTransactionId
      ),
    { staleTime: 0, showErrorToast: false }
  );

export const useLpTransferEditImpact = (
  fundId: string,
  fundTransactionId: string,
  change: LpTransferDraftChange | null
) =>
  useApiQuery<LpTransferImpact>(
    queryKeys.fundTransactions.lpTransferEditImpact(
      fundId,
      fundTransactionId,
      change
    ),
    () =>
      fundTransactionService.getLpTransferEditImpact(
        fundId,
        fundTransactionId,
        change!
      ),
    { enabled: change != null, staleTime: 0, showErrorToast: false }
  );

export const useUpdateLpTransferDraft = (
  fundId: string,
  fundTransactionId: string
) =>
  useApiMutation<LpTransferUpdateResponse, APIError, LpTransferDraftUpdate>(
    (update) =>
      fundTransactionService.updateLpTransferDraft(
        fundId,
        fundTransactionId,
        update
      ),
    {
      invalidateKeys: [
        ...lpTransferRecalculationKeys,
        queryKeys.fundTransactions.all,
      ],
      successMessage: 'Draft transfer updated',
      showErrorToast: false,
    }
  );
